import { ButtonHTMLAttributes, AnchorHTMLAttributes, MouseEvent } from 'react';
import { Link, LinkProps } from 'react-router-dom';

import styled from 'styled-components';

import { Color, Font, theme } from '@styles/theme';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'disabled'> {}
interface AnchorProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'color' | 'disabled'> {}

export type ActionTextBaseProps = {
  color?: Color;
  hoverColor?: Color;
  font?: Font;
  ellipsis?: boolean;
  isDisabled?: boolean;
  underline?: boolean;
  center?: boolean;
  text?: string;
};

interface ActionLinkProps extends ActionTextBaseProps, Omit<AnchorProps, 'href'> {
  to: LinkProps['to'];
}

export interface ActionTextProps extends ActionTextBaseProps, ButtonProps {
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}

export function ActionText(props: ActionTextProps) {
  const {
    font = 'regular16',
    color = 'elementBlue100',
    hoverColor = 'elementBlue100',
    ellipsis = false,
    isDisabled = false,
    underline = false,
    center = false,
    text,
    children,
    ...rest
  } = props;
  return (
    <StyledElement
      as="button"
      disabled={isDisabled}
      $font={font}
      $color={color}
      $hoverColor={hoverColor}
      $ellipsis={ellipsis}
      $underline={underline}
      $center={center}
      children={text ?? children}
      {...rest}
    />
  );
}

function ActionTextLink(props: ActionLinkProps) {
  const {
    font = 'regular16',
    color = 'elementBlue100',
    hoverColor = 'elementBlue100',
    ellipsis = false,
    isDisabled = false,
    underline = false,
    center = false,
    ...rest
  } = props;
  return (
    <StyledElement
      as={isDisabled ? 'span' : Link}
      $font={font}
      $color={color}
      $hoverColor={hoverColor}
      $ellipsis={ellipsis}
      $isDisabled={isDisabled}
      $underline={underline}
      $center={center}
      {...rest}
    />
  );
}

export interface ActionLinkExternalProps extends ActionTextBaseProps, AnchorProps {
  href: string;
}

function ActionTextLinkExternal(props: ActionLinkExternalProps) {
  const {
    font = 'regular16',
    color = 'elementBlue100',
    hoverColor = 'elementBlue100',
    ellipsis = false,
    isDisabled = false,
    underline = false,
    center = false,
    ...rest
  } = props;
  return (
    <StyledElement
      as={isDisabled ? 'span' : 'a'}
      $font={font}
      $color={color}
      $hoverColor={hoverColor}
      $ellipsis={ellipsis}
      $isDisabled={isDisabled}
      $underline={underline}
      $center={center}
      target="_blank"
      rel="noopener noreferrer"
      {...rest}
    />
  );
}

ActionText.Link = ActionTextLink;
ActionText.ExternalLink = ActionTextLinkExternal;

const StyledElement = styled.a<{
  $font: Font;
  $color: Color;
  $hoverColor: Color;
  $ellipsis: boolean;
  $isDisabled?: boolean;
  $underline?: boolean;
  $center?: boolean;
}>((p) => ({
  background: 'transparent',
  padding: 0,
  margin: 0,
  border: 'none',
  transition: 'all 150ms ease-out',
  display: 'inline-block',
  outline: 'none',

  font: theme.font[p.$font],
  color: theme.color[p.$color],
  textAlign: p.$center ? 'center' : 'left',

  ...(!p.$isDisabled && {
    '&:not(:disabled)': {
      '&:hover, &:focus': {
        cursor: 'pointer',
        color: theme.color[p.$hoverColor],
        textDecoration: 'underline',
      },
      ...(p.$underline && { textDecoration: 'underline' }),
    },
  }),

  ...(p.$ellipsis && {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
}));
